import { initialLaborIntakeData } from './laborIntakeSchema';

const DRAFT_STORAGE_KEY = 'wazin_labor_intake_draft_v2';
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const isLocalDevelopment =
  typeof window !== 'undefined' && LOCAL_HOSTS.includes(window.location.hostname);
const API_URL = isLocalDevelopment ? 'http://localhost:8001' : (process.env.REACT_APP_BACKEND_URL || '');

const clone = (value) => JSON.parse(JSON.stringify(value));

const createAttachmentId = (name, size) =>
  `${name}-${size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const sanitizeFiles = (files) =>
  Array.from(files || []).map((file) => ({
    id: createAttachmentId(file.name, file.size),
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    uploadedAt: new Date().toISOString(),
  }));

const sanitizeAttachmentItems = (items) =>
  Array.from(items || []).map((item) => ({
    id: item.id || createAttachmentId(item.fileName || item.name || 'attachment', item.size || 0),
    category: item.category || '',
    typeKey: item.typeKey || '',
    typeLabel: item.typeLabel || item.displayName || item.name || '',
    displayName: item.displayName || item.typeLabel || item.name || '',
    fileName: item.fileName || item.name || '',
    name: item.fileName || item.name || item.displayName || '',
    size: item.size || 0,
    type: item.mimeType || item.type || 'application/octet-stream',
    mimeType: item.mimeType || item.type || 'application/octet-stream',
    uploadedAt: item.uploadedAt || new Date().toISOString(),
    status: item.status || 'saved',
  }));

export const getEmptyLaborIntakeData = () => clone(initialLaborIntakeData);

export const saveLaborIntakeDraft = (draft) => {
  const draftPayload = {
    ...draft,
    attachments: {
      ...draft.attachments,
      items: sanitizeAttachmentItems(draft.attachments?.items || draft.attachments?.files || []),
    },
  };

  localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
    ...draftPayload,
    _savedAt: new Date().toISOString(),
  }));
};

export const getLaborIntakeDraft = () => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || 'null');
  } catch (error) {
    console.error('Failed to read labor intake draft:', error);
    return null;
  }
};

export const clearLaborIntakeDraft = () => {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
};

const readJson = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'تعذر تنفيذ طلب القضايا العمالية.');
  return data;
};

export const createLaborCase = async (formData) => {
  const payload = {
    ...formData,
    attachments: {
      ...formData.attachments,
      items: sanitizeAttachmentItems(formData.attachments.items || formData.attachments.files || []),
    },
  };
  const response = await fetch(`${API_URL}/api/labor-intake/requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const newCase = await readJson(response);
  clearLaborIntakeDraft();
  return newCase;
};

export const getLaborCaseByTracking = async ({ referenceNumber, trackingToken }) => {
  const query = new URLSearchParams({ reference_number: referenceNumber, tracking_token: trackingToken });
  const response = await fetch(`${API_URL}/api/labor-intake/requests/track?${query.toString()}`);
  if (response.status === 404) return null;
  return readJson(response);
};

export const addAdditionalDocuments = async ({ caseId, trackingToken, files }) => {
  const mappedFiles = sanitizeFiles(files);
  const response = await fetch(`${API_URL}/api/labor-intake/requests/${caseId}/additional-documents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tracking_token: trackingToken, documents: mappedFiles }),
  });
  return readJson(response);
};
